/**
 * Pure H · Canonical KPI card block.
 *
 * Standardised presentation of a KPI value that came from
 * `/api/pureh/kpi/summary`. Shows a subtle "canonical · collection"
 * source chip so viewers can distinguish canonical numbers from
 * legacy mock KPIs still present in the layout.
 */
import type { ReactNode } from "react";

export function CanonicalKpiCell({
  testId, label, value, unit, source, footnote, empty = false,
}: {
  testId:   string;
  label:    string;
  value:    number | null | undefined;
  unit?:    string | null;
  source:   string;             // e.g. "pureh_customer_payments"
  footnote?: ReactNode;
  empty?:   boolean;
}) {
  const display = (value === null || value === undefined)
    ? "—"
    : new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 2,
      }).format(value);

  return (
    <div data-testid={testId} className="min-w-0">
      <div className="flex items-center gap-2">
        <div
          className="text-[10px] uppercase tracking-[0.16em]"
          style={{ color: "var(--ph-ink-muted, #64748b)" }}
        >
          {label}
        </div>
        <span
          data-testid={`${testId}-canonical`}
          title={`Source of truth: ${source}`}
          style={{
            display: "inline-block",
            padding: "1px 5px",
            borderRadius: 3,
            fontSize: 9, fontWeight: 700,
            letterSpacing: "0.14em", textTransform: "uppercase",
            background: "#dbeafe", color: "#1e3a8a",
          }}
        >
          CANONICAL
        </span>
      </div>
      <div
        className="mt-1 text-xl font-semibold tabular-nums"
        style={{
          color: "var(--ph-ink-strong, #0f172a)",
          letterSpacing: "-0.01em",
        }}
      >
        {unit === "EGP" && !empty ? "EGP " : ""}{display}
        {unit && unit !== "EGP" && !empty ? ` ${unit}` : ""}
      </div>
      <div
        className="mt-1 text-[10px]"
        style={{ color: "var(--ph-ink-soft, #94a3b8)" }}
      >
        {source}
        {footnote ? <span className="ml-1">· {footnote}</span> : null}
      </div>
    </div>
  );
}
